import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Search, LogOut, Inbox, Phone, Mail, Clock, Calendar, ChevronDown, Loader2 } from "lucide-react";
import { firm } from "@/config/firm.config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nexprime_admin_token";

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusPill = ({ status }) => {
  const map = {
    sent: "border-emerald-400/40 text-emerald-300",
    failed: "border-red-400/40 text-red-300",
    skipped: "border-ink/30 text-ink/60",
  };
  return (
    <span
      data-testid="enquiry-status"
      className={`inline-block border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] ${map[status] || map.skipped}`}
    >
      {status || "—"}
    </span>
  );
};

const LoginScreen = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess();
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-900 px-6" data-testid="admin-login">
      <form
        onSubmit={submit}
        className="w-full max-w-md border border-white/10 bg-navy-800/60 p-8 md:p-10"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{firm.name}</p>
        <h1 className="mt-3 font-serif text-3xl text-paper">Enquiry Inbox</h1>
        <p className="mt-2 text-sm text-ink/70">Sign in to view submitted enquiries.</p>

        <label className="mt-8 block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">Email</label>
        <input
          type="email"
          required
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email-input"
          className="focus-ring mt-2 w-full border-b border-white/15 bg-transparent py-2.5 text-paper outline-none focus:border-gold"
        />

        <label className="mt-6 block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">Password</label>
        <input
          type="password"
          required
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="focus-ring mt-2 w-full border-b border-white/15 bg-transparent py-2.5 text-paper outline-none focus:border-gold"
        />

        {error && (
          <p className="mt-5 text-sm text-red-300" data-testid="admin-login-error">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="focus-ring mt-8 inline-flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-gold-light transition-colors duration-300 hover:bg-gold/20 disabled:opacity-50"
        >
          {loading && <Loader2 size={14} className="animate-spin" />}
          {loading ? "Signing in" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

const EnquiryCard = ({ e }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="border border-white/10 bg-navy-800/40" data-testid="enquiry-card">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="enquiry-toggle"
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-white/[0.03]"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h3 className="truncate font-serif text-lg text-paper" data-testid="enquiry-name">{e.full_name}</h3>
            <StatusPill status={e.email_status} />
          </div>
          <p className="mt-0.5 truncate text-sm text-gold/90">{e.subject}</p>
        </div>
        <span className="hidden shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] text-ink/50 sm:block">
          {fmtDate(e.created_at)}
        </span>
        <ChevronDown size={16} className={`shrink-0 text-ink/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-white/10 px-5 py-5" data-testid="enquiry-detail">
          <div className="grid gap-3 sm:grid-cols-2">
            <a href={`tel:${e.phone}`} className="focus-ring flex items-center gap-2 text-sm text-paper/90 hover:text-gold-light">
              <Phone size={14} className="text-gold" /> {e.phone}
            </a>
            {e.email ? (
              <a href={`mailto:${e.email}`} className="focus-ring flex items-center gap-2 break-all text-sm text-paper/90 hover:text-gold-light">
                <Mail size={14} className="text-gold" /> {e.email}
              </a>
            ) : (
              <span className="flex items-center gap-2 text-sm text-ink/50"><Mail size={14} className="text-gold" /> No email</span>
            )}
            <span className="flex items-center gap-2 text-sm text-paper/90">
              <Calendar size={14} className="text-gold" /> {e.preferred_datetime || "No preferred time"}
            </span>
            <span className="flex items-center gap-2 text-sm text-ink/60">
              <Clock size={14} className="text-gold" /> {fmtDate(e.created_at)}
            </span>
          </div>
          <div className="mt-5 border-t border-white/10 pt-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">Message</p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-paper/90" data-testid="enquiry-message">
              {e.description}
            </p>
          </div>
        </div>
      )}
    </li>
  );
};

const Inbox_ = ({ onLogout }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (term) => {
    setLoading(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        params: term ? { search: term } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(data.enquiries);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    const id = setTimeout(() => load(search), 300);
    return () => clearTimeout(id);
  }, [search, load]);

  return (
    <div className="min-h-screen bg-navy-900" data-testid="admin-inbox">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-navy-900/90 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <Inbox size={18} className="text-gold" />
            <div>
              <p className="font-serif text-lg leading-none text-paper">Enquiry Inbox</p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.25em] text-ink/50">{firm.name}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout"
            className="focus-ring inline-flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:border-gold/40 hover:text-gold-light"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-8">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/50" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, email or subject…"
            data-testid="admin-search-input"
            className="focus-ring w-full border border-white/10 bg-navy-800/50 py-3 pl-10 pr-4 text-sm text-paper outline-none placeholder:text-ink/40 focus:border-gold/40"
          />
        </div>

        <div className="mt-4 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50" data-testid="enquiry-count">
            {loading ? "Loading…" : `${enquiries.length} enquir${enquiries.length === 1 ? "y" : "ies"}`}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" /></div>
        ) : enquiries.length === 0 ? (
          <p className="py-20 text-center text-sm text-ink/50" data-testid="admin-empty">No enquiries found.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {enquiries.map((e) => <EnquiryCard key={e.id} e={e} />)}
          </ul>
        )}
      </main>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  const verify = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthed(false); setChecking(false); return; }
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setAuthed(true);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { verify(); }, [verify]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-navy-900"><Loader2 className="animate-spin text-gold" /></div>;
  }
  return authed ? <Inbox_ onLogout={logout} /> : <LoginScreen onSuccess={() => setAuthed(true)} />;
}
